import { Layout } from '@components/Layout';
import { NestedSidebar } from '@components/SidebarV2';
import type { NestedSidebarItem } from '@components/SidebarV2';
import PermissionGate from '@components/upercut-permissions/PermissionGate';
import {
    JobOfferTemplateAdd,
    JobOfferTemplateDetail,
    JobOfferTemplateEdit,
    JobOfferTemplateManagement,
} from '@modules/JobOfferTemplates';
import ProfileSkeleton from '@modules/talents/components/ProfileSkeleton';
import { Box, CircularProgress } from '@mui/material';
import { Stack } from '@mui/system';
import { useQuery } from '@apollo/client';
import { GET_JOB_OFFER_TEMPLATE } from '@gql/jobOfferTemplates';
import { useMemo } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom';

const JobOfferTemplatePage = () => {
    return (
        <Routes>
            <Route element={<JobOfferTemplateListLayout />}>
                <Route
                    path="/"
                    element={
                        <PermissionGate permission="job-offer-templates:read">
                            <JobOfferTemplateManagement />
                        </PermissionGate>
                    }
                />
                <Route
                    path="/add"
                    element={
                        <PermissionGate permission="job-offer-templates:create">
                            <JobOfferTemplateAdd />
                        </PermissionGate>
                    }
                />
                <Route path="/:slug" element={<JobOfferTemplateDetailsLayout />}>
                    <Route index element={<Navigate to="detail" replace />} />
                    <Route
                        path="detail"
                        element={
                            <PermissionGate permission="job-offer-templates:read">
                                <JobOfferTemplateDetail />
                            </PermissionGate>
                        }
                    />
                    <Route
                        path="edit"
                        element={
                            <PermissionGate permission="job-offer-templates:update">
                                <JobOfferTemplateEdit />
                            </PermissionGate>
                        }
                    />
                </Route>
            </Route>
        </Routes>
    );
};

const JobOfferTemplateListLayout = () => {
    return (
        <Layout
            breadcrumbs={[
                { label: 'Tableau de bord', href: '/', value: 'dashboard' },
                { label: 'Modèles d\'offres d\'emploi', href: '/job-offer-templates', value: 'job-offer-templates' },
                { label: 'Créer un modèle', href: '/job-offer-templates/add', value: 'add' },
                { label: 'Détails du modèle', href: '/job-offer-templates/:slug/detail', value: 'detail' },
            ]}
            heading="Gestion des modèles d'offres d'emploi"
        >
            <Outlet />
        </Layout>
    );
};

const JobOfferTemplateDetailsLayout = () => {
    const { slug } = useParams<{ slug: string }>();

    const { data: templateData, loading: loadingTemplate } = useQuery(GET_JOB_OFFER_TEMPLATE, {
        variables: { jobNumber: slug },
        skip: !slug,
    });

    const nestedItems: NestedSidebarItem[] = useMemo(
        () => [
            { id: 'detail', label: 'Info', icon: 'fi-rr-info', path: `/job-offer-templates/${slug}/detail` },
        ],
        [slug],
    );

    if (loadingTemplate) {
        return (
            <Stack sx={{ pt: 0, flex: 1, minHeight: '100%', gap: 3 }}>
                <ProfileSkeleton />
            </Stack>
        );
    }

    return (
        <Box sx={{ display: 'flex', flex: 1, minHeight: '100%', mx: '-24px' }}>
            <NestedSidebar
                items={nestedItems}
                title={templateData?.getJobOfferTemplatePro?.title ?? 'Modèle'}
            />
            <Stack flex={1} width={0} pt={0} minHeight="100%">
                <Box sx={{ flex: 1, pb: 3, display: 'flex', px: '24px', minWidth: 0 }}>
                    <Outlet />
                </Box>
            </Stack>
        </Box>
    );
};

export default JobOfferTemplatePage;
